/**
 * CSOProfileService - Dohvatanje i ažuriranje osnovnih podataka CSO organizacija, pretraga.
 */

import ApiService from '@/core/api/ApiService';
import { API_ENDPOINTS } from '@/core/api/endpoints';

class CSOProfileService {
  async getOrganization(organizationId) {
    try {
      if (!organizationId) {
        throw new Error('ID organizacije je obavezan');
      }

      const response = await ApiService.get(API_ENDPOINTS.CSO.DETAILS(organizationId));
      const raw = response.data || response;

      return {
        id: raw.id,
        name: raw.name || raw.naziv || '',
        userEmail: raw.user_email || raw.userEmail || '',
        userPhone: raw.user_phone || raw.userPhone || '',
        membershipInfo: raw.membership_info || raw.membershipInfo || '',
        membershipReference: raw.membership_reference || raw.membershipReference || '',
        workingGroups: Array.isArray(raw.working_groups)
          ? raw.working_groups.map(wg => ({
              name: wg.name || '',
              organ: wg.organ || '',
              year: wg.year || '',
            }))
          : Array.isArray(raw.workingGroups) ? raw.workingGroups : [],
        representativeName: raw.representative_name || raw.representativeName || '',
        memberName: raw.member_name || raw.memberName || '',
        memberEmail: raw.member_email || raw.memberEmail || '',
        memberPhone: raw.member_phone || raw.memberPhone || '',
        deputyName: raw.deputy_name || raw.deputyName || '',
        deputyEmail: raw.deputy_email || raw.deputyEmail || '',
        deputyPhone: raw.deputy_phone || raw.deputyPhone || '',
        contributionAreas: Array.isArray(raw.contribution_areas)
          ? raw.contribution_areas.map(ca => ({
              area: ca.area || '',
              contribution: ca.contribution || '',
            }))
          : Array.isArray(raw.contributionAreas) ? raw.contributionAreas : [],
        applicationForm: raw.application_form || raw.applicationForm || null,
        projectsForm: raw.projects_form || raw.projectsForm || null,
        publicationsForm: raw.publications_form || raw.publicationsForm || null,
        conflictOfInterestForm: raw.conflict_of_interest_form || raw.conflictOfInterestForm || null,
        status: raw.status || 'draft',
        ...raw,
      };
    } catch (error) {
      if (error.response && error.response.status === 401) {
        throw new Error('Korisnik nije prijavljen');
      }
      if (error.response && error.response.status === 404) {
        throw new Error('Organizacija nije pronađena');
      }
      throw error;
    }
  }

  async updateOrganization(organizationId, data) {
    try {
      if (!organizationId) {
        throw new Error('ID organizacije je obavezan');
      }

      const contactName = [data.contactFirstName, data.contactLastName].filter(Boolean).join(' ').trim() || undefined;
      const payload = {
        name: data.name,
        ...(contactName && { contact_name: contactName }),
        user_email: data.userEmail,
        user_phone: data.userPhone,
        membership_info: data.membershipInfo,
        membership_reference: data.membershipReference,
        working_groups: data.workingGroups,
        representative_name: data.representativeName,
        member_name: data.memberName,
        member_email: data.memberEmail,
        member_phone: data.memberPhone,
        deputy_name: data.deputyName,
        deputy_email: data.deputyEmail,
        deputy_phone: data.deputyPhone,
        contribution_areas: data.contributionAreas,
        status: data.status,
      };

      const response = await ApiService.put(API_ENDPOINTS.CSO.UPDATE(organizationId), payload);

      if (response.data) {
        return response.data;
      }
      return { success: true };
    } catch (error) {
      if (error.response && error.response.status === 401) {
        throw new Error('Korisnik nije prijavljen');
      }
      if (error.response && error.response.status === 404) {
        throw new Error('Organizacija nije pronađena');
      }
      if (error.response && error.response.status === 400) {
        const errorMessage = error.response.data?.message || 'Neispravni podaci organizacije';
        throw new Error(errorMessage);
      }
      throw error;
    }
  }

  async getAllOrganizations() {
    try {
      const response = await ApiService.get(API_ENDPOINTS.CSO.LIST);

      if (response.data) {
        if (response.data.csos && Array.isArray(response.data.csos)) {
          return response.data.csos.map(cso => ({
            id: cso.id,
            name: cso.name,
            naziv: cso.name,
            registrationNumber: cso.registration_number,
            registration_number: cso.registration_number,
            maticniBroj: cso.registration_number,
            register_id: cso.register_id,
            registry: cso.register_name,
            register_name: cso.register_name,
            registar: cso.register_name,
            ...cso
          }));
        }
        if (Array.isArray(response.data)) {
          return response.data;
        }
      }

      return [];
    } catch (error) {
      if (error.response && error.response.status === 401) {
        throw new Error('Korisnik nije prijavljen');
      }

      throw error;
    }
  }
}

const csoProfileServiceInstance = new CSOProfileService();
export default csoProfileServiceInstance;
